import i18n from 'localization';
import { legacyColors } from 'constants-colors';

type Purpose = 'analytics';

type ServiceTranslation = {
  title?: string;
  description?: string;
};

type Translations = {
  privacyPolicyUrl?: string;
  // Button texts
  acceptAll?: string;
  acceptSelected?: string;
  decline?: string;
  ok?: string;
  save?: string;
  close?: string;
  consentModal?: {
    description?: string;
    title?: string;
  };
  consentNotice?: {
    description?: string;
    learnMore?: string;
  };
  privacyPolicy?: {
    name?: string;
    text?: string;
  };
  purposeItem?: {
    service?: string;
    services?: string;
  };
  service?: {
    purpose?: string;
    purposes?: string;
    disableAll?: {
      title?: string;
      description?: string;
    };
  };
  poweredBy?: string;
  purposes?: Partial<Record<Purpose, ServiceTranslation>>;
};

type CookieRule = string | RegExp | [RegExp | string, string, string]; // [nameOrPattern, path, domain]

type ServiceConfig = {
  name: string;
  title?: string;
  purposes?: Purpose[];
  default?: boolean;
  required?: boolean;
  onlyOnce?: boolean;
  cookies?: CookieRule[];
  translations?: {
    zz?: ServiceTranslation;
    en?: ServiceTranslation;
    de?: ServiceTranslation;
  };
  // Klaro calls this when consent changes for the service
  callback?: (consent: boolean, service: { name: string }) => void;
  // Optional advanced flags used by Klaro
  optOut?: boolean;
  contextualConsentOnly?: boolean;
};

export type KlaroConfig = {
  elementID?: string;
  storageMethod?: 'cookie' | 'localStorage';
  storageName?: string;
  cookieDomain?: string;
  cookieExpiresAfterDays?: number;
  htmlTexts?: boolean;
  default?: boolean;
  lang?: string;
  mustConsent?: boolean;
  acceptAll?: boolean;
  hideDeclineAll?: boolean;
  hideLearnMore?: boolean;
  // Runtime CSS variable overrides for theming
  styling?: {
    theme?: string | string[];
    green1?: string; // Accept All button background
    blue1?: string; // Confirm Selection button background
    dark2?: string; // Decline all button background
  };
  translations?: {
    custom?: Translations;
  };
  services: ServiceConfig[];
};

declare global {
  interface Window {
    klaroConfig?: KlaroConfig;
  }
}

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  return window.location.hostname;
}

function getMixpanelCookieRules(domain?: string): CookieRule[] {
  // Match mp_*_mixpanel cookies on the current domain
  return domain ? [[/^mp_.*_mixpanel$/, '/', domain]] : [];
}

function getDatadogCookieRules(domain?: string): CookieRule[] {
  // Match _dd_* cookies on the current domain
  return domain ? [[/^_dd_.*$/, '/', domain]] : [];
}

const cookieDomain = getCookieDomain();

// Reuse colors from shared constants
const defaultPrimary = legacyColors.primary800;
const defaultSecondary = legacyColors.hyperlinkColor;
const defaultDark2 = legacyColors.neutral.grey300;

// Get Klaro translations using i18n
export function getKlaroTranslations(): Translations {
  return {
    // Button texts
    acceptAll: i18n.t('web.klaro.acceptAll'),
    acceptSelected: i18n.t('web.klaro.acceptSelected'),
    decline: i18n.t('web.klaro.decline'),
    ok: i18n.t('web.klaro.ok'),
    save: i18n.t('web.klaro.acceptSelected'),
    // Consent notice (banner)
    consentNotice: {
      description: i18n.t('web.klaro.consentNotice.description'),
      learnMore: i18n.t('web.klaro.consentNotice.learnMore'),
    },
    // Consent modal (detailed settings)
    consentModal: {
      title: i18n.t('application.name'),
      description: i18n.t('web.klaro.consentModal.description'),
    },
    // Privacy policy
    privacyPolicy: {
      name: i18n.t('web.klaro.privacyPolicy.name'),
      text: i18n.t('web.klaro.privacyPolicy.text'),
    },
    // Purpose item labels
    purposeItem: {
      service: i18n.t('web.klaro.purposeItem.service'),
      services: i18n.t('web.klaro.purposeItem.services'),
    },
    // Service labels
    service: {
      purpose: i18n.t('web.klaro.service.purpose'),
    },
    poweredBy: ' ',
    purposes: {
      analytics: {
        title: i18n.t('web.klaro.purposes.analytics.title'),
        description: i18n.t('web.klaro.purposes.analytics.description'),
      },
    },
  };
}

export const klaroConfig: KlaroConfig = {
  elementID: 'klaro',
  storageMethod: 'cookie',
  storageName: 'klaro',
  cookieExpiresAfterDays: 180,
  mustConsent: false,
  acceptAll: true,
  hideDeclineAll: false,
  default: false,
  lang: 'custom',
  styling: {
    theme: ['light', 'bottom'],
    green1: defaultPrimary, // Accept All button
    blue1: defaultSecondary, // Confirm Selection button
    dark2: defaultDark2, // Reject All button
  },
  // Translations are set at runtime in createThemedKlaroConfig() when i18n is ready
  translations: {},
  services: [
    {
      name: 'mixpanel',
      purposes: ['analytics'],
      default: false,
      cookies: getMixpanelCookieRules(cookieDomain),
      callback: (consent: boolean) => {
        if (consent) {
          import('../helpers/analytics').then(({ initializeMixpanel }) => {
            initializeMixpanel();
          });
        } else {
          import('../helpers/analytics').then(({ shutdownMixpanel }) => {
            shutdownMixpanel();
          });
        }
      },
    },
    {
      name: 'datadog-rum',
      purposes: ['analytics'],
      default: false,
      cookies: getDatadogCookieRules(cookieDomain),
      callback: (consent: boolean) => {
        if (consent) {
          import('../ddSpanMonitor').then(({ initializeDatadog }) => {
            initializeDatadog();
          });
        } else {
          import('../ddSpanMonitor').then(({ shutdownDatadog }) => {
            shutdownDatadog();
          });
        }
      },
    },
  ],
};

// Helper for setups that still rely on the CDN Klaro script with data-config="klaroConfig"
export function attachKlaroConfigToWindow(): void {
  if (typeof window === 'undefined') return;

  window.klaroConfig = klaroConfig;
}

/**
 * Whitelabel theme options for Klaro
 */
export type WhitelabelTheme = {
  primaryColor?: string; // Whitelabel primary color (used for Accept All button)
  secondaryColor?: string; // Whitelabel secondary color
  name?: string; // Whitelabel name (for modal title)
  privacyPolicyUrl?: string; // Custom privacy policy URL
};

export type KlaroTheme = {
  theme?: string | string[];
  green1: string; // Accept All button background
  blue1: string; // Confirm Selection button background
  dark2: string; // Decline All button background
};

/**
 * Creates a Klaro theme from whitelabel.
 * Falls back to VTS default colors if no whitelabel theme is provided.
 */
export function createKlaroTheme(
  whitelabelTheme?: WhitelabelTheme
): KlaroTheme {
  return {
    theme: ['light', 'bottom'],
    green1: whitelabelTheme?.primaryColor || defaultPrimary,
    blue1: whitelabelTheme?.secondaryColor || defaultSecondary,
    dark2: defaultDark2,
  };
}

type KlaroConfigOptions = {
  name?: string; // Modal title (e.g., whitelabel name)
  privacyPolicyUrl?: string;
};

/**
 * Creates a Klaro config with the provided theme and options.
 *
 * NOTE: Translations are fetched at runtime (when this function is called)
 * to ensure i18n is fully initialized with the correct locale.
 */
export function createKlaroConfig(
  klaroTheme: KlaroTheme,
  options?: KlaroConfigOptions
): KlaroConfig {
  const translations = getKlaroTranslations();

  return {
    ...klaroConfig,
    styling: klaroTheme,
    translations: {
      custom: {
        ...translations,
        consentModal: {
          ...translations.consentModal,
          title: options?.name || translations.consentModal?.title,
        },
        privacyPolicyUrl: options?.privacyPolicyUrl,
      },
    },
  };
}

/**
 * Convenience function that creates a themed Klaro config from whitelabel options.
 */
export function createThemedKlaroConfig(theme?: WhitelabelTheme): KlaroConfig {
  const klaroTheme = createKlaroTheme(theme);

  return createKlaroConfig(klaroTheme, {
    name: theme?.name,
    privacyPolicyUrl: theme?.privacyPolicyUrl,
  });
}
